/**
 * Mobile UI controller — powers mobile.html.
 *
 * Handles the bottom-sheet drawer, tab switching, audio sources,
 * look presets, composition, effects toggles, reel recording, and
 * now-playing overlay text.
 */

import { AudioEngine } from "../audio/sources";
import { Renderer } from "../viz/renderer";
import {
  SCENES,
  scenesByMood,
  resolveSceneValues,
  type ScenePreset,
  type SceneVariant,
} from "../viz/scenes/scenePresets";
import {
  CanvasRecorder,
  suggestFilename,
} from "../record/capture";
import { RecordingCompositor } from "../record/recordingCompositor";
import {
  SOCIAL_PRESETS,
  getSocialPreset,
  suggestSocialFilename,
} from "../record/socialPresets";
import {
  NowPlayingOverlay,
  OverlayConfig,
  DEFAULT_OVERLAY,
} from "./nowPlaying";
import { haptic } from "./haptics";
import { shareFile } from "./share";
import { buildMobileAdvancedPanel } from "./mobileAdvancedPanel";
import { createPadContainer, type PadContainerHandle, type PadAudioFeatures } from "./padContainer";

// ---------------------------------------------------------------------------
// Local-storage keys for UX persistence
// ---------------------------------------------------------------------------

const LS_LAST_TAB = "pulsefx_mobile_last_tab";
const LS_LAYER_GROUPS_OPEN = "pulsefx_mobile_layer_groups_open";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MobileUIArgs {
  engine: AudioEngine;
  renderer: Renderer;
}

// ---------------------------------------------------------------------------
// Quick-start aspect/length configs
// ---------------------------------------------------------------------------
//
// The canonical platform list lives in `record/socialPresets.ts` so the
// mobile Reel UI and the desktop Reel Studio share specs (aspect, length,
// filename slug, share text). Mobile only consumes `aspect` + `lengthSec`
// from the preset for the live framing change; filename + share text are
// applied in the record-stop handler below via `getSocialPreset`.
void SOCIAL_PRESETS;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function $<T extends HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Public init
// ---------------------------------------------------------------------------

export function initMobileUI({ engine, renderer }: MobileUIArgs): void {
  const frame = document.getElementById("frame") as HTMLElement;

  // ---- Splash screen: fade out as soon as we're wired up ----
  // PWA launches show a blank canvas for ~1-2 frames; the splash masks
  // that with a branded gradient until init completes.
  const splash = document.getElementById("mbSplash");
  if (splash) {
    requestAnimationFrame(() => {
      splash.classList.add("is-ready");
      // Remove from DOM after the fade so it's not a layout/paint cost.
      setTimeout(() => splash.remove(), 1100);
    });
  }

  // ---- Overlay ----
  const overlay = new NowPlayingOverlay(frame);
  let overlayCfg: OverlayConfig = { ...DEFAULT_OVERLAY };
  overlay.setConfig(overlayCfg);

  // -------- Empty state: hide once preview starts --------
  const emptyState = document.getElementById("mbEmptyState");
  let sourceLoaded = false;
  let emptyStateDismissed = false;
  const dismissEmptyState = (): void => {
    if (emptyStateDismissed) return;
    emptyStateDismissed = true;
    if (emptyState) {
      emptyState.classList.add("is-hidden");
      setTimeout(() => emptyState.remove(), 400);
    }
  };
  const markSourceLoaded = (): void => {
    if (sourceLoaded) return;
    sourceLoaded = true;
    dismissEmptyState();
    // Reveal the mini-player now that there's something to control.
    showMiniPlayer();
  };

  // -------- Drawer open/close (with real touch dragging) --------
  const drawer = $<HTMLElement>("mbDrawer");
  const drawerToggle = $<HTMLButtonElement>("mbDrawerToggle");
  const drawerPill = $<HTMLElement>("mbDrawerPill");
  let drawerOpen = false;

  const openDrawer = (): void => {
    drawerOpen = true;
    drawer.style.transform = "";
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    drawerToggle.classList.add("is-open");
    drawerToggle.setAttribute("aria-expanded", "true");
    haptic("light");
    // Hide the mini-player while the drawer is open (drawer has all controls).
    hideMiniPlayer();
  };

  const closeDrawer = (): void => {
    drawerOpen = false;
    drawer.style.transform = "";
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    drawerToggle.classList.remove("is-open");
    drawerToggle.setAttribute("aria-expanded", "false");
    haptic("light");
    if (sourceLoaded) showMiniPlayer();
  };

  drawerToggle.addEventListener("click", () => {
    void engine.resume();
    if (drawerOpen) closeDrawer();
    else openDrawer();
  });

  // Real-time drawer drag: follows the finger. The drag handle (the pill)
  // is the active region; the rest of the drawer scrolls normally so
  // users can use the controls without accidentally dismissing.
  let dragStartY = 0;
  let dragLastY = 0;
  let dragging = false;
  const dragHandle = drawerPill ?? drawer; // fall back to whole drawer
  dragHandle.addEventListener(
    "touchstart",
    (e) => {
      dragStartY = e.touches[0]?.clientY ?? 0;
      dragLastY = dragStartY;
      dragging = true;
      drawer.classList.add("is-dragging");
    },
    { passive: true },
  );
  dragHandle.addEventListener(
    "touchmove",
    (e) => {
      if (!dragging) return;
      const y = e.touches[0]?.clientY ?? 0;
      dragLastY = y;
      const dy = Math.max(0, y - dragStartY); // only drag downward
      drawer.style.transform = `translateY(${dy}px)`;
    },
    { passive: true },
  );
  dragHandle.addEventListener(
    "touchend",
    () => {
      if (!dragging) return;
      dragging = false;
      drawer.classList.remove("is-dragging");
      const dy = dragLastY - dragStartY;
      // Threshold: drag past 100px → close. Otherwise snap back open.
      if (dy > 100) {
        closeDrawer();
      } else {
        // Snap back to open by clearing the inline transform.
        drawer.style.transform = "";
      }
    },
    { passive: true },
  );

  // -------- Settings: drawer opacity, auto-fade, haptics --------
  // These are pure UX prefs (not visualizer state) so they live in
  // localStorage rather than renderer.params. The CSS uses
  // --mb-drawer-bg-opacity on documentElement; haptic + auto-fade are
  // read by the rest of this module.
  const SETTINGS_KEY = "pulsefx_mobile_settings_v1";
  interface MobileSettings {
    drawerOpacity: number;   // 0.30..1.00, drives the CSS var
    autoFadeDrawer: boolean; // fade drawer to 58% while interacting
    haptics: boolean;        // disable to silence vibration on Android
  }
  const DEFAULT_SETTINGS: MobileSettings = {
    drawerOpacity: 0.96,
    autoFadeDrawer: false,
    haptics: true,
  };
  let settings: MobileSettings = { ...DEFAULT_SETTINGS };
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) settings = { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  const persistSettings = (): void => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      /* private mode etc. */
    }
  };
  const applyDrawerOpacity = (): void => {
    document.documentElement.style.setProperty(
      "--mb-drawer-bg-opacity",
      String(settings.drawerOpacity),
    );
  };
  applyDrawerOpacity();

  const drawerOpacityInput = $<HTMLInputElement>("mb-drawerOpacity");
  const drawerOpacityVal = $<HTMLElement>("mb-drawerOpacityVal");
  if (drawerOpacityInput) {
    drawerOpacityInput.value = String(settings.drawerOpacity);
    if (drawerOpacityVal) drawerOpacityVal.textContent = settings.drawerOpacity.toFixed(2);
    drawerOpacityInput.addEventListener("input", () => {
      settings.drawerOpacity = Number(drawerOpacityInput.value);
      applyDrawerOpacity();
      if (drawerOpacityVal) drawerOpacityVal.textContent = settings.drawerOpacity.toFixed(2);
      persistSettings();
    });
  }

  const autoFadeInput = $<HTMLInputElement>("mb-autoFadeDrawer");
  if (autoFadeInput) {
    autoFadeInput.checked = settings.autoFadeDrawer;
    autoFadeInput.addEventListener("change", () => {
      settings.autoFadeDrawer = autoFadeInput.checked;
      persistSettings();
      // If turning off mid-interaction, drop any active fade right away.
      if (!settings.autoFadeDrawer) drawer.classList.remove("is-previewing");
    });
  }

  const hapticsInput = $<HTMLInputElement>("mb-haptics");
  if (hapticsInput) {
    hapticsInput.checked = settings.haptics;
    hapticsInput.addEventListener("change", () => {
      settings.haptics = hapticsInput.checked;
      persistSettings();
      // Note: the haptic() helper itself is a no-op on iOS Safari today,
      // so this mainly affects Android Chrome users.
    });
  }

  // -------- First-launch tutorial overlay --------
  // 3-step coachmark that walks through Source → Look → Reel. Auto-shown
  // on first launch; can be replayed from Settings.
  const TUTORIAL_KEY = "pulsefx_tutorial_seen_v1";
  interface TutorialStep {
    title: string;
    body: string;
  }
  const TUTORIAL_STEPS: TutorialStep[] = [
    {
      title: "Welcome to PulseFX",
      body:
        "Reactive visuals for any music. We'll walk through it in <strong>3 steps</strong>.",
    },
    {
      title: "1. Pick your audio",
      body:
        "The <strong>Source</strong> tab loads music — Mic for live audio, Load File for a saved track, or paste a Media URL.",
    },
    {
      title: "2. Pick a scene",
      body:
        "The <strong>Look</strong> tab has 24+ hand-tuned scenes. Tap any pad to switch instantly. Tweak Layers + Fine Tuning if you want to dial it in.",
    },
    {
      title: "3. Record + share",
      body:
        "The <strong>Reel</strong> tab → pick TikTok / Reels / Spotify Canvas → tap Record → tap again to stop. The iOS share sheet pops up with your <strong>.mp4</strong>.",
    },
  ];
  const tutEl = document.getElementById("mbTutorial");
  const tutStepEl = document.getElementById("mb-tutorial-step");
  const tutTitleEl = document.getElementById("mb-tutorial-title");
  const tutBodyEl = document.getElementById("mb-tutorial-body");
  const tutSkipBtn = $<HTMLButtonElement>("mb-tutorial-skip");
  const tutNextBtn = $<HTMLButtonElement>("mb-tutorial-next");
  let tutIndex = 0;

  const showTutorialStep = (i: number): void => {
    if (!tutEl) return;
    tutIndex = i;
    const step = TUTORIAL_STEPS[i];
    if (tutStepEl) tutStepEl.textContent = `${i + 1} of ${TUTORIAL_STEPS.length}`;
    if (tutTitleEl) tutTitleEl.textContent = step.title;
    if (tutBodyEl) tutBodyEl.innerHTML = step.body;
    if (tutNextBtn) {
      tutNextBtn.textContent = i === TUTORIAL_STEPS.length - 1 ? "Got it" : "Next";
    }
  };
  const openTutorial = (): void => {
    if (!tutEl) return;
    showTutorialStep(0);
    tutEl.classList.add("is-open");
    tutEl.setAttribute("aria-hidden", "false");
    haptic("light");
  };
  const closeTutorial = (markSeen: boolean): void => {
    if (!tutEl) return;
    tutEl.classList.remove("is-open");
    tutEl.setAttribute("aria-hidden", "true");
    if (markSeen) {
      try {
        localStorage.setItem(TUTORIAL_KEY, "1");
      } catch {
        /* ignore */
      }
    }
  };
  tutNextBtn?.addEventListener("click", () => {
    haptic("tick");
    if (tutIndex < TUTORIAL_STEPS.length - 1) {
      // Open the relevant tab as the user advances so they SEE what we're
      // describing — Source on step 1, Look on step 2, Reel on step 3.
      const tabForStep = ["source", "source", "look", "reel"][tutIndex + 1];
      if (tabForStep) switchToTab(tabForStep);
      // Make sure the drawer is open so the highlighted tab is visible.
      if (!drawerOpen) openDrawer();
      showTutorialStep(tutIndex + 1);
    } else {
      closeTutorial(true);
    }
  });
  tutSkipBtn?.addEventListener("click", () => {
    haptic("light");
    closeTutorial(true);
  });
  // "Replay" button in Settings.
  document.getElementById("mb-replayTutorial")?.addEventListener("click", () => {
    haptic("light");
    openTutorial();
  });

  // Legacy library favorites — collapsible accordion in the Look tab that
  // shows user-starred presets from the standalone preset previewer.
  // Reads/writes `pulsefx.legacyFavorites` from localStorage; same key
  // used by the previewer and the desktop creator so they all stay in sync.
  setupMobileLegacyFavorites();
  // First-launch auto-show: a moment after the splash + empty state
  // settle, pop the tutorial. Skips itself if the user has seen it.
  let tutorialSeen = false;
  try {
    tutorialSeen = localStorage.getItem(TUTORIAL_KEY) === "1";
  } catch {
    /* ignore */
  }
  if (!tutorialSeen) {
    // Wait until the empty-state CTA lands so the tutorial doesn't fight it.
    setTimeout(openTutorial, 2400);
  }

  // -------- Auto-fade drawer while adjusting (opt-in) --------
  // Replaces the old smart-shrink behavior — instead of moving the
  // drawer (which yanked controls out from under the user's finger),
  // we just fade the drawer's opacity to ~58% while a slider is being
  // touched. Layout stays put, finger stays on the same control, but
  // the live canvas reads through the chrome.
  let previewSnapBack: ReturnType<typeof setTimeout> | null = null;
  const enterPreview = (): void => {
    if (!settings.autoFadeDrawer) return;
    if (previewSnapBack !== null) {
      clearTimeout(previewSnapBack);
      previewSnapBack = null;
    }
    drawer.classList.add("is-previewing");
  };
  const queueExitPreview = (): void => {
    if (previewSnapBack !== null) clearTimeout(previewSnapBack);
    previewSnapBack = setTimeout(() => {
      drawer.classList.remove("is-previewing");
      previewSnapBack = null;
    }, 650);
  };
  const PREVIEW_SELECTOR =
    'input[type="range"], .mb-advSegBtn, .mb-zoomSlider, .mb-zoomBtn';
  const isPreviewTrigger = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false;
    return Boolean(target.closest(PREVIEW_SELECTOR));
  };
  drawer.addEventListener("touchstart", (e) => {
    if (isPreviewTrigger(e.target)) enterPreview();
  }, { passive: true, capture: true });
  drawer.addEventListener("input", (e) => {
    if (isPreviewTrigger(e.target)) enterPreview();
  });
  document.addEventListener("touchend", queueExitPreview, { passive: true });
  document.addEventListener("touchcancel", queueExitPreview, { passive: true });
  document.addEventListener("pointerup", queueExitPreview, { passive: true });

  // -------- Tab switching (with localStorage persistence) --------
  const tabs = document.querySelectorAll<HTMLButtonElement>(".mb-tab");
  const panels = document.querySelectorAll<HTMLElement>(".mb-tabPanel");

  // -------- Universal PadContainer (mounted lazily inside Look tab) --------
  // Lives in <details id="mb-padsDetails"> so the Look tab still opens fast
  // on Scenes. Mount on details-open AND when the Look tab is active; unmount
  // on tab switch-away or details close so the iframe stops using GPU/audio.
  const padsDetails = document.getElementById("mb-padsDetails") as HTMLDetailsElement | null;
  const padsHost = document.getElementById("mb-padsHost");
  let padsHandle: PadContainerHandle | null = null;
  const padsAudioSource = (): PadAudioFeatures => {
    const f = engine.read();
    return {
      bass: f.bass, mid: f.mid, treble: f.treble, level: f.level,
      beat: f.beat, bpm: f.bpm, beatCount: f.beatCount,
      t: performance.now(),
    };
  };
  const mountPads = (): void => {
    if (padsHandle || !padsHost) return;
    try {
      padsHandle = createPadContainer({
        root: padsHost,
        layout: "mobile",
        audioSource: padsAudioSource,
      });
    } catch (err) {
      console.error("[pads] mobile mount failed:", err);
    }
  };
  const unmountPads = (): void => {
    if (!padsHandle) return;
    try { padsHandle.destroy(); } catch { /* ignore */ }
    padsHandle = null;
    if (padsHost) padsHost.innerHTML = "";
  };
  if (padsDetails) {
    padsDetails.addEventListener("toggle", () => {
      if (padsDetails.open) mountPads(); else unmountPads();
    });
  }

  const switchToTab = (target: string): void => {
    if (target === "look") dismissEmptyState();
    tabs.forEach((t) => {
      const active = t.dataset.tab === target;
      t.classList.toggle("is-active", active);
      t.setAttribute("aria-selected", String(active));
    });
    panels.forEach((p) => {
      const match = p.id === `mbTab-${target}`;
      p.hidden = !match;
      p.classList.toggle("is-active", match);
    });
    // Tear down pads iframe whenever the user leaves the Look tab so it
    // isn't quietly burning GPU + holding audio in the background.
    if (target !== "look") {
      unmountPads();
    } else if (padsDetails?.open) {
      mountPads();
    }
    try {
      localStorage.setItem(LS_LAST_TAB, target);
    } catch {
      /* private mode etc. */
    }
  };

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      const target = tab.dataset.tab;
      if (!target) return;
      haptic("tick");
      switchToTab(target);
    });
  });

  // Restore last-used tab on launch.
  try {
    const lastTab = localStorage.getItem(LS_LAST_TAB);
    if (lastTab && ["source", "look", "reel", "settings"].includes(lastTab)) {
      switchToTab(lastTab);
    }
  } catch {
    /* ignore */
  }

  // -------- Layer-group disclosure: persist open/closed state --------
  // The user's preferred categories should stick across sessions so they
  // don't have to re-expand the same group every time.
  const layerGroups = document.querySelectorAll<HTMLDetailsElement>(".mb-layerGroup");
  let openGroups = new Set<string>();
  try {
    const saved = JSON.parse(localStorage.getItem(LS_LAYER_GROUPS_OPEN) ?? "[]");
    if (Array.isArray(saved)) openGroups = new Set(saved);
  } catch {
    /* ignore */
  }
  layerGroups.forEach((g, i) => {
    const key = (g.querySelector("summary")?.textContent ?? `g${i}`).trim();
    // Only override default `open` if we have saved state.
    if (openGroups.size > 0) g.open = openGroups.has(key);
    g.addEventListener("toggle", () => {
      haptic("tick");
      const updated = new Set<string>();
      layerGroups.forEach((g2, i2) => {
        const k = (g2.querySelector("summary")?.textContent ?? `g${i2}`).trim();
        if (g2.open) updated.add(k);
      });
      try {
        localStorage.setItem(LS_LAYER_GROUPS_OPEN, JSON.stringify([...updated]));
      } catch {
        /* ignore */
      }
    });
  });

  // -------- Mini-player floating bar --------
  // Visible when drawer is closed AND a source is loaded. Provides
  // one-tap access to scenes / play-pause / record without re-opening
  // the full drawer for casual use.
  const miniPlayer = document.getElementById("mbMiniPlayer");
  const miniSceneBtn = $<HTMLButtonElement>("mbMiniSceneBtn");
  const miniSceneName = $<HTMLElement>("mbMiniSceneName");
  const miniPlayBtn = $<HTMLButtonElement>("mbMiniPlayBtn");
  const miniRecBtn = $<HTMLButtonElement>("mbMiniRecBtn");

  const showMiniPlayer = (): void => {
    if (miniPlayer && sourceLoaded && !drawerOpen) {
      miniPlayer.classList.add("is-visible");
      miniPlayer.setAttribute("aria-hidden", "false");
      // Hide the drawer toggle pill — the mini-player has its own
      // entry point (the scene name) and stacking two pills looks busy.
      drawerToggle.style.display = "none";
    }
  };
  const hideMiniPlayer = (): void => {
    if (miniPlayer) {
      miniPlayer.classList.remove("is-visible");
      miniPlayer.setAttribute("aria-hidden", "true");
      drawerToggle.style.display = "";
    }
  };

  miniSceneBtn?.addEventListener("click", () => {
    haptic("light");
    openDrawer();
    switchToTab("look");
  });

  // Empty-state Get Started button → open drawer on Source tab.
  document.getElementById("mbStartBtn")?.addEventListener("click", () => {
    haptic("medium");
    void engine.resume();
    dismissEmptyState();
    openDrawer();
    switchToTab("source");
  });

  // Tap the HUD to manually hide it (e.g. for cleaner-feeling preview).
  const hudEl = document.getElementById("hud");
  hudEl?.addEventListener("click", () => {
    hudEl.classList.toggle("is-tapped-hidden");
  });

  // -------- Audio sources --------
  const setSourceStatus = (text: string): void => {
    const el = $<HTMLElement>("mb-sourceStatus");
    if (el) el.textContent = text;
  };

  const setSourceActive = (kind: string): void => {
    document.querySelectorAll<HTMLButtonElement>(".mb-srcBtn").forEach((btn) => {
      btn.classList.remove("is-active");
    });
    const mic = $<HTMLButtonElement>("mb-sourceMic");
    const tab = $<HTMLButtonElement>("mb-sourceTab");
    if (kind === "mic" && mic) mic.classList.add("is-active");
    if (kind === "tab" && tab) tab.classList.add("is-active");
  };

  $<HTMLButtonElement>("mb-sourceMic")?.addEventListener("click", async () => {
    setSourceStatus("Requesting mic…");
    haptic("light");
    try {
      const info = await engine.useMicrophone();
      setSourceStatus(`● ${info.label}`);
      setSourceActive("mic");
      markSourceLoaded();
      haptic("medium");
    } catch (err) {
      setSourceStatus(`Mic error: ${err instanceof Error ? err.message : String(err)}`);
    }
  });

  const tabBtn = $<HTMLButtonElement>("mb-sourceTab");
  // Detect if tab audio is available; if not, show a hint.
  if (!(navigator.mediaDevices as MediaDevices & { getDisplayMedia?: unknown }).getDisplayMedia) {
    if (tabBtn) tabBtn.disabled = true;
    const hint = $<HTMLElement>("mb-tabHint");
    if (hint) hint.hidden = false;
  }
  tabBtn?.addEventListener("click", async () => {
    setSourceStatus("Requesting tab audio…");
    haptic("light");
    try {
      const info = await engine.useTabAudio();
      setSourceStatus(`● ${info.label}`);
      setSourceActive("tab");
      markSourceLoaded();
      haptic("medium");
    } catch (err) {
      setSourceStatus(err instanceof Error ? err.message : String(err));
    }
  });

  // ----- Play/pause button binding -----
  // Single source of truth: one click handler at a time on `mb-playPauseAudio`,
  // always pointed at the currently-loaded audio element. The replaceWith
  // trick wipes any previous listener so we never leak stacked handlers.
  const playBtn = $<HTMLButtonElement>("mb-playPauseAudio");

  const bindPlayButton = (audio: HTMLAudioElement | null): void => {
    if (!playBtn) return;
    if (!audio) {
      playBtn.hidden = true;
      return;
    }
    // Clone-and-replace strips any previous listener from prior file/URL loads.
    const fresh = playBtn.cloneNode(true) as HTMLButtonElement;
    playBtn.replaceWith(fresh);
    fresh.hidden = false;
    fresh.textContent = audio.paused ? "▶︎ Tap to play" : "⏸ Pause";

    fresh.addEventListener("click", async () => {
      try {
        // iOS Safari leaves the AudioContext suspended until a gesture-initiated
        // resume completes. Awaiting here keeps the gesture chain alive.
        await engine.resume();
        if (audio.paused) {
          await audio.play();
          fresh.textContent = "⏸ Pause";
        } else {
          audio.pause();
          fresh.textContent = "▶︎ Play";
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setSourceStatus(`Playback blocked: ${msg}`);
      }
    });

    audio.addEventListener("play", () => {
      fresh.textContent = "⏸ Pause";
      if (miniPlayBtn) miniPlayBtn.textContent = "⏸";
    });
    audio.addEventListener("pause", () => {
      fresh.textContent = "▶︎ Play";
      if (miniPlayBtn) miniPlayBtn.textContent = "▶︎";
    });
    audio.addEventListener("ended", () => {
      fresh.textContent = "▶︎ Play";
      if (miniPlayBtn) miniPlayBtn.textContent = "▶︎";
    });
  };

  // ----- Playback bar (scrub + A/B loop + beat-snap) -----
  // Bound whenever a file or URL source loads. The bar lives in the
  // Source tab and provides:
  //   - Live time / duration readout
  //   - Draggable scrub slider (jumps audio.currentTime)
  //   - ⟲5 / 5⟳ jump buttons
  //   - Loop toggle → reveals A/B sliders + beat-snap controls
  //   - Beat-snap: when on, A/B snap to the nearest bar boundary
  //     computed from the live BPM (so the loop is always musical)
  const playback = {
    container: document.getElementById("mb-playback"),
    timeEl: document.getElementById("mb-playback-time"),
    durEl: document.getElementById("mb-playback-duration"),
    scrub: $<HTMLInputElement>("mb-playback-scrub"),
    back: $<HTMLButtonElement>("mb-playback-back"),
    fwd: $<HTMLButtonElement>("mb-playback-fwd"),
    loop: $<HTMLInputElement>("mb-playback-loop"),
    loopRangeA: document.getElementById("mb-playback-loopRange"),
    loopRangeB: document.getElementById("mb-playback-loopRangeB"),
    loopStart: $<HTMLInputElement>("mb-playback-loopStart"),
    loopEnd: $<HTMLInputElement>("mb-playback-loopEnd"),
    setA: $<HTMLButtonElement>("mb-playback-setA"),
    setB: $<HTMLButtonElement>("mb-playback-setB"),
    beatSnapRow: document.getElementById("mb-playback-beatSnap"),
    snap: $<HTMLInputElement>("mb-playback-snap"),
    snapBars: $<HTMLSelectElement>("mb-playback-snapBars"),
  };
  /** Format seconds as M:SS. */
  const fmtTime = (sec: number): string => {
    if (!isFinite(sec) || sec < 0) sec = 0;
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };
  let playbackBoundAudio: HTMLAudioElement | null = null;
  let playbackPollHandle: ReturnType<typeof setInterval> | null = null;
  let userIsScrubbing = false;
  /** Snap a time (seconds) to the nearest bar boundary at current BPM. */
  const snapToBeat = (sec: number, bpm: number, bars: number): number => {
    if (!bpm || bpm <= 0) return sec;
    const beatLen = 60 / bpm;
    const barLen = beatLen * bars;
    return Math.round(sec / barLen) * barLen;
  };

  const refreshLoopRowVisibility = (): void => {
    const on = playback.loop?.checked ?? false;
    if (playback.loopRangeA) playback.loopRangeA.hidden = !on;
    if (playback.loopRangeB) playback.loopRangeB.hidden = !on;
    if (playback.beatSnapRow) playback.beatSnapRow.hidden = !on;
  };
  refreshLoopRowVisibility();
  playback.loop?.addEventListener("change", () => {
    haptic("tick");
    refreshLoopRowVisibility();
  });

  const bindPlaybackBar = (audio: HTMLAudioElement | null): void => {
    if (!playback.container) return;
    playbackBoundAudio = audio;
    if (!audio) {
      playback.container.hidden = true;
      if (playbackPollHandle) {
        clearInterval(playbackPollHandle);
        playbackPollHandle = null;
      }
      return;
    }
    playback.container.hidden = false;

    // Initialize duration once metadata loads.
    const updateDuration = (): void => {
      const dur = audio.duration;
      if (isFinite(dur) && dur > 0 && playback.durEl) {
        playback.durEl.textContent = fmtTime(dur);
      }
    };
    audio.addEventListener("loadedmetadata", updateDuration);
    if (audio.readyState >= 1) updateDuration();

    // Poll currentTime to drive the scrub slider + time readout. 200ms
    // is plenty smooth for a transport bar and stays cheap on battery.
    if (playbackPollHandle) clearInterval(playbackPollHandle);
    playbackPollHandle = setInterval(() => {
      if (!playbackBoundAudio) return;
      const a = playbackBoundAudio;
      const dur = a.duration;
      if (!isFinite(dur) || dur <= 0) return;
      const cur = a.currentTime;
      // Update scrub UNLESS the user is actively scrubbing it.
      if (!userIsScrubbing && playback.scrub) {
        playback.scrub.value = String((cur / dur) * 100);
      }
      if (playback.timeEl) playback.timeEl.textContent = fmtTime(cur);

      // Loop enforcement: if past loop end, jump back to loop start.
      if (playback.loop?.checked && playback.loopStart && playback.loopEnd) {
        let startPct = Number(playback.loopStart.value);
        let endPct = Number(playback.loopEnd.value);
        if (endPct <= startPct) endPct = Math.min(100, startPct + 1);
        let startSec = (startPct / 100) * dur;
        let endSec = (endPct / 100) * dur;
        // Apply beat snap if enabled and we have a BPM from the analyzer.
        if (playback.snap?.checked) {
          const features = engine.read();
          const bpm = features.bpm;
          const bars = Number(playback.snapBars?.value ?? 8);
          if (bpm > 0) {
            startSec = snapToBeat(startSec, bpm, bars);
            endSec = snapToBeat(endSec, bpm, bars);
            if (endSec - startSec < (60 / bpm) * bars) {
              // Ensure at least one bar of loop length.
              endSec = startSec + (60 / bpm) * bars;
            }
          }
        }
        if (cur >= endSec || cur < startSec) {
          a.currentTime = startSec;
        }
      }
    }, 200);
  };

  // Wire the scrub slider — drag = scrub through audio.
  playback.scrub?.addEventListener("pointerdown", () => { userIsScrubbing = true; });
  playback.scrub?.addEventListener("touchstart", () => { userIsScrubbing = true; }, { passive: true });
  const finishScrub = (): void => {
    if (!userIsScrubbing) return;
    userIsScrubbing = false;
    const a = playbackBoundAudio;
    if (!a || !playback.scrub) return;
    const dur = a.duration;
    if (!isFinite(dur) || dur <= 0) return;
    const pct = Number(playback.scrub.value);
    a.currentTime = (pct / 100) * dur;
  };
  playback.scrub?.addEventListener("pointerup", finishScrub);
  playback.scrub?.addEventListener("touchend", finishScrub, { passive: true });
  playback.scrub?.addEventListener("input", () => {
    // Live time readout while scrubbing (no seek until release for perf).
    const a = playbackBoundAudio;
    if (!a || !playback.timeEl) return;
    const dur = a.duration;
    if (!isFinite(dur) || dur <= 0) return;
    const pct = Number(playback.scrub.value);
    playback.timeEl.textContent = fmtTime((pct / 100) * dur);
  });

  // ⟲5 / 5⟳ jump buttons.
  playback.back?.addEventListener("click", () => {
    haptic("light");
    if (playbackBoundAudio) {
      playbackBoundAudio.currentTime = Math.max(0, playbackBoundAudio.currentTime - 5);
    }
  });
  playback.fwd?.addEventListener("click", () => {
    haptic("light");
    if (playbackBoundAudio) {
      const dur = playbackBoundAudio.duration;
      const next = playbackBoundAudio.currentTime + 5;
      playbackBoundAudio.currentTime = isFinite(dur) ? Math.min(dur, next) : next;
    }
  });
  // ⊙ A / ⊙ B → snap loop bound to current playhead.
  playback.setA?.addEventListener("click", () => {
    haptic("medium");
    if (playbackBoundAudio && playback.loopStart) {
      const dur = playbackBoundAudio.duration;
      if (isFinite(dur) && dur > 0) {
        playback.loopStart.value = String((playbackBoundAudio.currentTime / dur) * 100);
      }
    }
  });
  playback.setB?.addEventListener("click", () => {
    haptic("medium");
    if (playbackBoundAudio && playback.loopEnd) {
      const dur = playbackBoundAudio.duration;
      if (isFinite(dur) && dur > 0) {
        playback.loopEnd.value = String((playbackBoundAudio.currentTime / dur) * 100);
      }
    }
  });
  playback.snap?.addEventListener("change", () => haptic("tick"));

  $<HTMLInputElement>("mb-fileInput")?.addEventListener("change", async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    setSourceStatus(`Loading ${file.name}…`);
    try {
      // Wake the AudioContext inside the change-handler call stack so iOS
      // accepts subsequent audio.play() calls without a separate tap.
      await engine.resume();
      const info = engine.useFile(file);
      setSourceStatus(`● ${info.label}`);
      setSourceActive("file");
      bindPlayButton(info.mediaElement ?? null);
      bindPlaybackBar(info.mediaElement ?? null);
      markSourceLoaded();
      haptic("medium");
      // Surface load errors (corrupt files, unsupported codecs).
      if (info.mediaElement) {
        info.mediaElement.addEventListener("error", () => {
          setSourceStatus(
            `Couldn't decode "${file.name}". Try MP3, M4A, WAV, or OGG.`,
          );
        });
      }
    } catch (err) {
      setSourceStatus(`File error: ${err instanceof Error ? err.message : String(err)}`);
    }
  });

  $<HTMLButtonElement>("mb-mediaUrlLoad")?.addEventListener("click", async () => {
    const input = $<HTMLInputElement>("mb-mediaUrl");
    const raw = input?.value.trim() ?? "";
    if (!raw) return;
    setSourceStatus("Loading URL…");
    try {
      await engine.resume();
      const info = engine.useRemoteUrl(raw);
      setSourceStatus(`● ${info.label}`);
      setSourceActive("url");
      bindPlayButton(info.mediaElement ?? null);
      bindPlaybackBar(info.mediaElement ?? null);
      markSourceLoaded();
      haptic("medium");
      if (info.mediaElement) {
        // Catch CORS / network failures from the audio element.
        info.mediaElement.addEventListener("error", () => {
          setSourceStatus(
            "URL blocked: the host doesn't allow direct playback (CORS). " +
              "Download the file and use 'Load audio file' instead.",
          );
        });
      }
    } catch (err) {
      setSourceStatus(`URL error: ${err instanceof Error ? err.message : String(err)}`);
    }
  });

  // ---- Look: preset baseline ----
  // Each preset/segment tap clears these "sticky" fields first so a previous
  // preset never silently bleeds into the next pick. Without this, e.g.
  // tapping a scene that disables the warp tunnel would leave warp on if a
  // previous scene had it enabled.
  const STICKY_KEYS: Array<keyof typeof renderer.params> = [
    "conceptMode",
    "showShockwave",
    "showStarbursts",
    "layerParticles",
    "layerStrings",
    "layerLattice",
    "layerAurora",
    "layerWarp",
    "layerCathedral",
    "layerDnaHelix",
    "layerVortex",
    "layerTide",
    "layerOrbital",
    "layerGalaxy",
    "layerAuroraForest",
    "layerBoids",
    "layerWind",
    "layerMercury",
    "layerMountains",
    "layerWaveform",
  ];
  const STICKY_DEFAULTS = {
    conceptMode: "none" as const,
    showShockwave: false,
    showStarbursts: false,
    layerParticles: true,
    layerStrings: false,
    layerLattice: false,
    layerAurora: false,
    layerWarp: false,
    layerCathedral: false,
    layerDnaHelix: false,
    layerVortex: false,
    layerTide: false,
    layerOrbital: false,
    layerGalaxy: false,
    layerAuroraForest: false,
    layerBoids: false,
    layerWind: false,
    layerMercury: false,
    layerMountains: false,
    layerWaveform: false,
  };

  // Track which curated scene the user last applied so the active state
  // survives across preset taps without us having to deep-equal the params.
  let activeSceneName: string | null = null;
  let activeVariantName: string | null = null;
  const sceneButtons: Array<{ name: string; el: HTMLButtonElement }> = [];
  const refreshSceneActive = (): void => {
    for (const { name, el } of sceneButtons) {
      el.classList.toggle("is-active", name === activeSceneName);
    }
    renderVariantStrip();
  };

  const applyPresetClean = (
    values: Partial<typeof renderer.params>,
    sceneName: string | null = null,
    variantName: string | null = null,
    sceneType: Renderer["sceneType"] = "bioclast",
  ): void => {
    renderer.sceneType = sceneType;
    // Reset sticky fields, then layer the preset on top. The preset itself
    // re-overrides any sticky key it cares about (e.g. cloud presets force
    // visualMode 0 + conceptMode "none").
    for (const k of STICKY_KEYS) {
      (renderer.params as unknown as Record<string, unknown>)[k as string] =
        (STICKY_DEFAULTS as Record<string, unknown>)[k as string];
    }
    Object.assign(renderer.params, values);
    activeSceneName = sceneName;
    activeVariantName = variantName;
    refreshLookUI();
    refreshSceneActive();
    dismissEmptyState();
    closeDrawer();
    // Surface the active scene name in the mini-player.
    if (miniSceneName && sceneName) {
      miniSceneName.textContent = variantName
        ? `${sceneName} · ${variantName}`
        : sceneName;
    }
    haptic("medium");
  };

  // -------- Look: curated scene pads --------
  // Single primary entry point. Each scene is a hand-tuned, self-contained
  // configuration — no chimera states from layering Particle Cloud + Hybrid
  // composition + Tunnel concept (which used to confuse the visuals).
  const scenePadsEl = $<HTMLElement>("mb-scenePads");
  // Variant chip strip is injected dynamically just under the pad grid so
  // we don't have to touch mobile.html. It only becomes visible when the
  // active scene actually has variants.
  let variantStripEl: HTMLDivElement | null = null;
  if (scenePadsEl?.parentElement) {
    variantStripEl = document.createElement("div");
    variantStripEl.className = "mb-variantStrip";
    variantStripEl.hidden = true;
    // Inline styles keep this self-contained — no CSS file edits required.
    variantStripEl.style.cssText =
      "display:flex;gap:6px;flex-wrap:wrap;padding:6px 0 0 0;";
    scenePadsEl.parentElement.insertBefore(variantStripEl, scenePadsEl.nextSibling);
  }

  const renderVariantStrip = (): void => {
    if (!variantStripEl) return;
    variantStripEl.innerHTML = "";
    const scene = activeSceneName
      ? SCENES.find((s) => s.name === activeSceneName)
      : null;
    if (!scene || !scene.variants?.length) {
      variantStripEl.hidden = true;
      return;
    }
    variantStripEl.hidden = false;
    const makeChip = (
      label: string,
      hue: number,
      isActive: boolean,
      onTap: () => void,
    ): HTMLButtonElement => {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "mb-variantChip" + (isActive ? " is-active" : "");
      chip.textContent = label;
      chip.style.cssText =
        "padding:6px 12px;border-radius:999px;font:500 12px/1 system-ui,sans-serif;" +
        `border:1px solid hsla(${hue}deg,80%,60%,${isActive ? 0.95 : 0.4});` +
        `background:hsla(${hue}deg,80%,${isActive ? 35 : 16}%,${isActive ? 0.55 : 0.25});` +
        `color:hsl(${hue}deg,90%,${isActive ? 92 : 78}%);cursor:pointer;`;
      chip.addEventListener("click", () => {
        onTap();
        haptic("light");
      });
      return chip;
    };
    // Base "Original"
    variantStripEl.appendChild(
      makeChip("Original", scene.accent, activeVariantName === null, () => {
        applyPresetClean(resolveSceneValues(scene, null), scene.name, null, scene.sceneType ?? "bioclast");
      }),
    );
    for (const v of scene.variants) {
      const hue = v.accent ?? scene.accent;
      variantStripEl.appendChild(
        makeChip(v.name, hue, activeVariantName === v.name, () => {
          applyPresetClean(resolveSceneValues(scene, v), scene.name, v.name, scene.sceneType ?? "bioclast");
        }),
      );
    }
  };

  if (scenePadsEl) {
    const engineHeading = document.createElement("div");
    engineHeading.className = "mb-sectionLabel mb-sectionLabel--sub";
    engineHeading.textContent = "Engines";
    engineHeading.style.cssText =
      "font:600 11px/1 system-ui,sans-serif;letter-spacing:.08em;" +
      "text-transform:uppercase;opacity:.6;margin:10px 0 6px 0;";
    scenePadsEl.appendChild(engineHeading);

    const engineRow = document.createElement("div");
    engineRow.className = "mb-padsTall mb-padsTall--row";
    engineRow.style.cssText =
      "display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:8px;";
    scenePadsEl.appendChild(engineRow);

    const galaxyBtn = document.createElement("button");
    galaxyBtn.type = "button";
    galaxyBtn.className = "mb-padTall";
    galaxyBtn.style.borderColor = "hsla(175deg, 90%, 62%, 0.5)";
    galaxyBtn.innerHTML = `
      <span class="mb-padTall__name" style="color:hsl(175deg, 90%, 78%)">Galaxy</span>
      <span class="mb-padTall__sub">Wall Lab particle river · main renderer</span>
    `;
    galaxyBtn.addEventListener("click", () => {
      renderer.sceneType = "galaxy";
      Object.assign(renderer.params, {
        paletteHue: 198,
        starSize: 0.34,
        opacity: 0.9,
        flowSpeed: 1.05,
        bassGravity: 1.4,
        midCurl: 1.25,
        trebleShimmer: 1.3,
        bloomEnabled: true,
        bloomStrength: 0.9,
        bloomRadius: 0.72,
        feedbackStrength: 0.04,
      });
      activeSceneName = "Galaxy";
      activeVariantName = null;
      refreshLookUI();
      refreshSceneActive();
      dismissEmptyState();
      closeDrawer();
      if (miniSceneName) miniSceneName.textContent = "Galaxy";
      haptic("medium");
    });
    engineRow.appendChild(galaxyBtn);
    sceneButtons.push({ name: "Galaxy", el: galaxyBtn });

    // Render mood-grouped sections so the pad grid feels curated, not flat.
    for (const group of scenesByMood()) {
      const heading = document.createElement("div");
      heading.className = "mb-sectionLabel mb-sectionLabel--sub";
      heading.textContent = group.mood;
      heading.style.cssText =
        "font:600 11px/1 system-ui,sans-serif;letter-spacing:.08em;" +
        "text-transform:uppercase;opacity:.6;margin:10px 0 6px 0;";
      scenePadsEl.appendChild(heading);
      const row = document.createElement("div");
      row.className = "mb-padsTall mb-padsTall--row";
      row.style.cssText =
        "display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:8px;";
      scenePadsEl.appendChild(row);
      for (const scene of group.scenes) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "mb-padTall";
        btn.style.borderColor = `hsla(${scene.accent}deg, 80%, 60%, 0.4)`;
        const variantHint = scene.variants?.length
          ? ` <span class="mb-padTall__variantBadge" style="opacity:.7;font-size:10px">×${scene.variants.length + 1}</span>`
          : "";
        btn.innerHTML = `
          <span class="mb-padTall__name" style="color:hsl(${scene.accent}deg, 80%, 78%)">${scene.name}${variantHint}</span>
          <span class="mb-padTall__sub">${scene.subtitle}</span>
        `;
        btn.addEventListener("click", () => {
          applyPresetClean(resolveSceneValues(scene, null), scene.name, null, scene.sceneType ?? "bioclast");
        });
        row.appendChild(btn);
        sceneButtons.push({ name: scene.name, el: btn });
      }
    }
  }
  // Touch types so unused-import lints stay quiet on builds where the
  // helper types are inferred via the variant-strip closure above.
  void (null as ScenePreset | SceneVariant | null);

  // -------- Look: per-layer toggles --------
  // The single source of truth for visibility. Each toggle drives one of
  // the layer* params and the renderer reads them every frame.
  type LayerKey =
    | "layerParticles"
    | "layerStrings"
    | "layerLattice"
    | "layerAurora"
    | "layerWarp"
    | "layerCathedral";
  const layerInputs = Array.from(
    document.querySelectorAll<HTMLInputElement>("#mb-layerList input[data-layer]"),
  );
  layerInputs.forEach((input) => {
    const key = input.dataset.layer as LayerKey | undefined;
    if (!key) return;
    input.addEventListener("change", () => {
      renderer.params[key] = input.checked;
      // Flipping any layer means we're no longer "exactly" on a scene preset.
      activeSceneName = null;
      activeVariantName = null;
      refreshSceneActive();
      haptic("tick");
    });
  });

  // -------- Look: effects toggles --------
  const shockwaveToggle = $<HTMLInputElement>("mb-showShockwave");
  const starburstToggle = $<HTMLInputElement>("mb-showStarbursts");
  const bloomToggle = $<HTMLInputElement>("mb-bloomEnabled");
  shockwaveToggle?.addEventListener("change", () => {
    renderer.params.showShockwave = shockwaveToggle.checked;
  });
  starburstToggle?.addEventListener("change", () => {
    renderer.params.showStarbursts = starburstToggle.checked;
  });
  bloomToggle?.addEventListener("change", () => {
    renderer.params.bloomEnabled = bloomToggle.checked;
  });

  // -------- Camera zoom (mobile slider) --------
  // Pinch-to-zoom on the canvas works but is finicky and the new wide
  // zoom range (0.15 lava-lamp .. 3.5 close-up) needed an explicit
  // surface so users can dial it precisely. The previous +/- buttons
  // were redundant with the slider + pinch and have been removed.
  const zoomSlider = $<HTMLInputElement>("mb-zoom");
  zoomSlider?.addEventListener("input", () => {
    renderer.params.cameraZoom = Number(zoomSlider.value);
  });

  const refreshLookUI = (): void => {
    // Sync layer toggles with current params (called after a scene apply).
    layerInputs.forEach((input) => {
      const key = input.dataset.layer as LayerKey | undefined;
      if (key) input.checked = Boolean(renderer.params[key]);
    });
    if (shockwaveToggle) shockwaveToggle.checked = renderer.params.showShockwave;
    if (starburstToggle) starburstToggle.checked = renderer.params.showStarbursts;
    if (bloomToggle) bloomToggle.checked = renderer.params.bloomEnabled;
    if (zoomSlider) zoomSlider.value = String(renderer.params.cameraZoom);
    // Re-sync every advanced slider/toggle so a scene apply updates them.
    advPanel?.refresh();
  };

  // -------- Advanced (fine-tuning) controls --------
  // Mounts every slider/toggle the desktop look panel exposes into a
  // collapsible mobile-styled panel. Sections like "Look", "Audio
  // Reactivity", "Camera", "Bloom Glow", "Atmosphere" all live here.
  const advHost = document.getElementById("mb-advPanel");
  const advPanel = advHost
    ? buildMobileAdvancedPanel(advHost, renderer)
    : null;

  refreshLookUI();

  // Pinch on the canvas mutates cameraZoom directly — keep the slider in
  // sync so the on-screen control matches what the gesture did.
  setInterval(() => {
    if (
      zoomSlider &&
      Math.abs(Number(zoomSlider.value) - renderer.params.cameraZoom) > 0.01
    ) {
      zoomSlider.value = String(renderer.params.cameraZoom);
    }
  }, 250);

  // -------- Reel: quick-start --------
  let armedSocialPresetId: string | null = null;
  document.querySelectorAll<HTMLButtonElement>("[data-quickstart]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const key = btn.dataset.quickstart;
      const preset = getSocialPreset(key);
      if (!preset) return;
      frame.dataset.aspect = preset.aspect;
      window.dispatchEvent(new Event("resize"));
      lengthCapMs = preset.lengthSec * 1000;
      armedSocialPresetId = preset.id;
      // Highlight only the chosen platform chip until the next pick.
      document
        .querySelectorAll<HTMLButtonElement>("[data-quickstart]")
        .forEach((b) => b.classList.remove("is-armed"));
      btn.classList.add("is-armed");
      haptic("light");
    });
  });

  // -------- Reel: record --------
  const recorder = new CanvasRecorder();
  // Composite the WebGL canvas + Now Playing overlay (text + watermark)
  // into a single canvas so the recorder captures both. Without this,
  // `canvas.captureStream()` only sees WebGL pixels and silently drops
  // the DOM overlay.
  const compositor = new RecordingCompositor(renderer.getCanvas(), {
    nowPlaying: overlay,
  });
  let lengthCapMs = 0;
  let recordingStartedAt = 0;
  let timerHandle: ReturnType<typeof setInterval> | null = null;

  const setRecStatus = (text: string): void => {
    const el = $<HTMLElement>("mb-recordStatus");
    if (el) el.textContent = text;
  };

  const recordBtn = $<HTMLButtonElement>("mb-recordBtn");

  /**
   * Try to share via the native iOS share sheet (Capacitor) or Web Share
   * API (PWA), falling back to download if neither succeeds. Uses the
   * unified `shareFile` helper so behavior matches the desktop reel
   * studio + future native exports.
   */
  const offerShareOrDownload = async (
    blob: Blob,
    filename: string,
  ): Promise<void> => {
    const armed = getSocialPreset(armedSocialPresetId);
    await shareFile(blob, filename, {
      title: armed ? `PulseFX · ${armed.label}` : "PulseFX clip",
      text: armed?.shareText ?? "Made with PulseFX",
    });
    haptic("heavy");
  };

  const toggleRecord = async (): Promise<void> => {
    if (recorder.isRecording()) {
      try {
        const result = await recorder.stop();
        compositor.stop();
        const armed = getSocialPreset(armedSocialPresetId);
        const filename = armed
          ? suggestSocialFilename(result.extension, armed)
          : suggestFilename(result.extension);
        // Try the share sheet first (works on Android + iOS for files
        // when the gesture chain is intact). Falls back to download.
        await offerShareOrDownload(result.blob, filename);
        setRecStatus(
          `Saved (${(result.blob.size / 1_000_000).toFixed(1)} MB, ${(result.durationMs / 1000).toFixed(1)}s).`,
        );
      } catch (err) {
        setRecStatus(`Error: ${err instanceof Error ? err.message : String(err)}`);
      }
      if (timerHandle !== null) {
        clearInterval(timerHandle);
        timerHandle = null;
      }
      recordBtn?.classList.remove("is-recording");
      miniRecBtn?.classList.remove("is-recording");
      document.body.classList.remove("is-recording");
      if (recordBtn) {
        (recordBtn.querySelector(".mb-recordBtn__label") as HTMLElement).textContent = "Record";
      }
    } else {
      // Start recording: separate video and audio streams.
      // File / URL sources put their audio in `recordStream` (a dedicated
      // MediaStreamAudioDestinationNode); mic / tab sources put it in
      // `stream` directly. We prefer recordStream so file playback
      // captures sound; without this fallback, recorded MP4s of file
      // sources are silent.
      const audioInfo = engine.info();
      compositor.start();
      recorder.start({
        videoStream: compositor.captureStream(60),
        audioStream: audioInfo.recordStream ?? audioInfo.stream ?? undefined,
      });
      recordingStartedAt = performance.now();
      recordBtn?.classList.add("is-recording");
      miniRecBtn?.classList.add("is-recording");
      // Auto-hide the HUD + chrome during recording for clean output.
      document.body.classList.add("is-recording");
      haptic("heavy");
      if (recordBtn) {
        (recordBtn.querySelector(".mb-recordBtn__label") as HTMLElement).textContent = "Stop";
      }
      timerHandle = setInterval(() => {
        if (!recorder.isRecording()) return;
        const elapsedMs = performance.now() - recordingStartedAt;
        const elapsedSec = elapsedMs / 1000;
        if (lengthCapMs > 0) {
          const remaining = Math.max(0, (lengthCapMs - elapsedMs) / 1000);
          setRecStatus(`● REC ${formatTime(elapsedSec)} / ${formatTime(lengthCapMs / 1000)} (−${formatTime(remaining)})`);
          if (elapsedMs >= lengthCapMs) void toggleRecord();
        } else {
          setRecStatus(`● REC ${formatTime(elapsedSec)}`);
        }
      }, 500);
    }
  };

  recordBtn?.addEventListener("click", () => void toggleRecord());

  // Mini-player record button mirrors the main one so users can record
  // without opening the drawer.
  miniRecBtn?.addEventListener("click", () => void toggleRecord());

  // Mini-player play/pause button. Finds the currently-bound audio
  // element from the engine and toggles its play state.
  miniPlayBtn?.addEventListener("click", async () => {
    haptic("light");
    const info = engine.info();
    const audio = info.mediaElement;
    if (!audio) {
      // No file/URL source loaded yet — open the drawer to the source tab.
      openDrawer();
      switchToTab("source");
      return;
    }
    try {
      await engine.resume();
      if (audio.paused) {
        await audio.play();
        if (miniPlayBtn) miniPlayBtn.textContent = "⏸";
      } else {
        audio.pause();
        if (miniPlayBtn) miniPlayBtn.textContent = "▶︎";
      }
    } catch {
      /* surface via source status if needed */
    }
  });

  // -------- Reel: snapshot --------
  $<HTMLButtonElement>("mb-snapshotBtn")?.addEventListener("click", () => {
    haptic("medium");
    void compositor.snapshot().then((blob) => {
      if (blob) void offerShareOrDownload(blob, suggestFilename("png"));
    });
  });

  // -------- Now Playing overlay --------
  const overlayEnabled = $<HTMLInputElement>("mb-overlayEnabled");
  const overlayTitle = $<HTMLInputElement>("mb-overlayTitle");
  const overlayArtist = $<HTMLInputElement>("mb-overlayArtist");

  const syncOverlay = (): void => {
    overlayCfg = {
      ...overlayCfg,
      enabled: overlayEnabled?.checked ?? false,
      title: overlayTitle?.value.trim() ?? "",
      artist: overlayArtist?.value.trim() ?? "",
    };
    overlay.setConfig(overlayCfg);
  };

  overlayEnabled?.addEventListener("change", syncOverlay);
  overlayTitle?.addEventListener("input", syncOverlay);
  overlayArtist?.addEventListener("input", syncOverlay);
}

// ─────────────────────────────────────────────────────────────────────────────
// Mobile Legacy Library: favorites accordion
//
// Mirrors the desktop creator's Legacy pane. Reads from the shared
// `pulsefx.legacyFavorites` localStorage key written by the standalone
// preset previewer (docs/extracted-avs/previewer.html).
// ─────────────────────────────────────────────────────────────────────────────

interface MobileLegacyFavRecord {
  key: string;
  name: string;
  fmt: "milk" | "avs" | "builtin";
  pack: string;
  url?: string;
  added: number;
}

const MB_LEGACY_FAV_KEY = "pulsefx.legacyFavorites";
const MB_LEGACY_LIB_URL_KEY = "pulsefx.legacyLibraryUrl";
const MB_LEGACY_LIB_DEFAULT = "http://127.0.0.1:9090/docs/extracted-avs/previewer.html";

function mbLoadLegacyFavs(): MobileLegacyFavRecord[] {
  try {
    const raw = localStorage.getItem(MB_LEGACY_FAV_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function mbSaveLegacyFavs(records: MobileLegacyFavRecord[]): void {
  try { localStorage.setItem(MB_LEGACY_FAV_KEY, JSON.stringify(records)); } catch { /* ignore */ }
}

function mbImportedLegacyKey(name: string): string {
  return "imported:" + name.toLowerCase().replace(/^[★☆\s"']+/, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function mbParseLegacyFavoritesInput(input: string): MobileLegacyFavRecord[] {
  try {
    const arr = JSON.parse(input);
    if (!Array.isArray(arr)) throw new Error("Expected an array");
    return arr
      .filter((r) => r && typeof r.name === "string")
      .map((r) => ({
        key: typeof r.key === "string" ? r.key : mbImportedLegacyKey(r.name),
        name: r.name,
        fmt: r.fmt === "avs" || r.fmt === "builtin" ? r.fmt : "milk",
        pack: typeof r.pack === "string" ? r.pack : "Legacy",
        url: typeof r.url === "string" ? r.url : "",
        added: typeof r.added === "number" ? r.added : Date.now(),
      }));
  } catch {
    return input.split(/\n+/)
      .map((line) => line.replace(/^[★☆\s]+/, "").trim())
      .filter(Boolean)
      .map((name) => ({
        key: mbImportedLegacyKey(name),
        name,
        fmt: "milk",
        pack: "Imported name list",
        url: "",
        added: Date.now(),
      }));
  }
}

function mbMergeLegacyFavs(records: MobileLegacyFavRecord[]): void {
  const existing = mbLoadLegacyFavs();
  const seen = new Set(existing.map(r => r.key));
  const merged = [...existing];
  for (const r of records) {
    if (r && r.key && !seen.has(r.key)) { merged.push(r); seen.add(r.key); }
  }
  mbSaveLegacyFavs(merged);
}

function mbImportLegacyFavoritesFromUrl(): boolean {
  const hash = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
  const params = new URLSearchParams(hash || window.location.search);
  const encoded = params.get("legacyFavorites");
  if (!encoded) return false;
  try {
    mbMergeLegacyFavs(mbParseLegacyFavoritesInput(encoded));
    const clean = window.location.pathname + window.location.search;
    window.history.replaceState(null, document.title, clean);
    return true;
  } catch { return false; }
}

function mbGetLegacyLibUrl(): string {
  try {
    const url = localStorage.getItem(MB_LEGACY_LIB_URL_KEY);
    if (url && /^https?:\/\//.test(url)) return url;
  } catch { /* ignore */ }
  return MB_LEGACY_LIB_DEFAULT;
}

function setupMobileLegacyFavorites(): void {
  const list = document.getElementById("mb-legacyList");
  const empty = document.getElementById("mb-legacyEmpty");
  const count = document.getElementById("mb-legacyCount");
  const browse = document.getElementById("mb-legacyBrowse");
  if (!list) return;

  const render = (): void => {
    const favs = mbLoadLegacyFavs();
    list.innerHTML = "";
    if (count) count.textContent = String(favs.length);
    if (empty) empty.style.display = favs.length ? "none" : "";
    const sorted = [...favs].sort((a, b) => (b.added || 0) - (a.added || 0));
    for (const fav of sorted) {
      const li = document.createElement("li");
      li.className = "mb-legacy__item";
      const fmt = document.createElement("span");
      fmt.className = "mb-legacy__itemFmt mb-legacy__itemFmt--" + fav.fmt;
      fmt.textContent = fav.fmt === "milk" ? "milk" : fav.fmt === "builtin" ? "built" : "avs";
      const name = document.createElement("span");
      name.className = "mb-legacy__itemName";
      name.textContent = fav.name;
      li.appendChild(fmt);
      li.appendChild(name);
      li.addEventListener("click", () => {
        const base = mbGetLegacyLibUrl();
        const sep = base.includes("?") ? "&" : "?";
        const param = fav.key.startsWith("imported:") || !fav.url
          ? `search=${encodeURIComponent(fav.name)}`
          : `preset=${encodeURIComponent(fav.key)}`;
        window.open(`${base}${sep}${param}`, "pulsefx-legacy-library");
      });
      list.appendChild(li);
    }
  };

  mbImportLegacyFavoritesFromUrl();
  render();
  window.addEventListener("storage", (e) => {
    if (e.key === MB_LEGACY_FAV_KEY) render();
  });
  window.addEventListener("message", (e) => {
    if (e.data && e.data.type === "pulsefx.legacyFavorites" && Array.isArray(e.data.records)) {
      mbSaveLegacyFavs(e.data.records);
      render();
    }
  });

  browse?.addEventListener("click", () => {
    window.open(mbGetLegacyLibUrl(), "pulsefx-legacy-library");
  });
}
